import { AnthropicApiError, determineAnthropicBucketWidth, fetchAllAnthropicUsage, type AnthropicUsageData } from '../anthropic-api';
import { anthropicCache } from '../cache-service';
import { readAdminCredentialRevision } from '../storage/admin-credential-revision';
import type { AnthropicBucketWidth, TimeRange, AnthropicUsageSegmentPayload } from './types';
import {
  ANTHROPIC_USAGE_SEGMENT_CACHE_VERSION,
  ANTHROPIC_GROUP_BY,
  ANTHROPIC_SEGMENT_CACHE_TTL_RECENT_MS,
  ANTHROPIC_SEGMENT_CACHE_TTL_HISTORICAL_MS,
  DAY_SECONDS,
  BUCKET_SECONDS_BY_WIDTH,
  MAX_BUCKETS_BY_WIDTH,
  delay
} from './constants';
import { debugLog } from '../debug-log';

// ── Time Range Utilities ─────────────────────────────

export const mergeTimeRanges = (ranges: TimeRange[]): TimeRange[] => {
  if (!ranges.length) return [];
  const sorted = [...ranges]
    .filter(range => Number.isFinite(range.start) && Number.isFinite(range.end) && range.end > range.start)
    .sort((a, b) => a.start - b.start);
  if (!sorted.length) return [];

  const merged: TimeRange[] = [sorted[0]];
  for (let i = 1; i < sorted.length; i += 1) {
    const last = merged[merged.length - 1];
    const current = sorted[i];
    if (current.start <= last.end) {
      last.end = Math.max(last.end, current.end);
    } else {
      merged.push({ ...current });
    }
  }
  return merged;
};

export const subtractTimeRanges = (target: TimeRange, coveredRanges: TimeRange[]): TimeRange[] => {
  const covered = mergeTimeRanges(coveredRanges);
  if (!covered.length) return [target];

  const missing: TimeRange[] = [];
  let cursor = target.start;
  for (const range of covered) {
    if (range.end <= cursor) continue;
    if (range.start >= target.end) break;
    if (range.start > cursor) {
      missing.push({ start: cursor, end: Math.min(range.start, target.end) });
    }
    cursor = Math.max(cursor, range.end);
    if (cursor >= target.end) break;
  }
  if (cursor < target.end) {
    missing.push({ start: cursor, end: target.end });
  }
  return missing.filter(range => range.end > range.start);
};

export const splitTimeRangesByMaxSpan = (ranges: TimeRange[], maxSpanSeconds: number): TimeRange[] => {
  const result: TimeRange[] = [];
  for (const range of ranges) {
    let cursor = range.start;
    while (cursor < range.end) {
      const end = Math.min(cursor + maxSpanSeconds, range.end);
      result.push({ start: cursor, end });
      cursor = end;
    }
  }
  return result;
};

export const splitTimeRangesAtBoundary = (ranges: TimeRange[], boundarySec: number): TimeRange[] => {
  const result: TimeRange[] = [];
  for (const range of ranges) {
    if (boundarySec <= range.start || boundarySec >= range.end) {
      result.push(range);
      continue;
    }
    result.push({ start: range.start, end: boundarySec });
    result.push({ start: boundarySec, end: range.end });
  }
  return result.filter(range => range.end > range.start);
};

// ── Time Helpers ─────────────────────────────────────

export const toUnixSeconds = (value: Date): number => Math.floor(value.getTime() / 1000);

export const getTodayStartUtcSec = (): number => {
  const nowSec = Math.floor(Date.now() / 1000);
  return Math.floor(nowSec / DAY_SECONDS) * DAY_SECONDS;
};

export const getAnthropicFinalizationBoundarySec = (_bucketWidth: AnthropicBucketWidth): number => (
  getTodayStartUtcSec()
);

export const isFinalizedAnthropicSegment = (
  bucketWidth: AnthropicBucketWidth,
  segmentEndTimeSec: number
): boolean => (
  segmentEndTimeSec <= getAnthropicFinalizationBoundarySec(bucketWidth)
);

export const resolveAnthropicSegmentCacheTtl = (
  bucketWidth: AnthropicBucketWidth,
  segmentEndTimeSec: number
): number => {
  return isFinalizedAnthropicSegment(bucketWidth, segmentEndTimeSec)
    ? ANTHROPIC_SEGMENT_CACHE_TTL_HISTORICAL_MS
    : ANTHROPIC_SEGMENT_CACHE_TTL_RECENT_MS;
};

// ── Segment Normalization & Dedup ────────────────────

export const normalizeAnthropicSegmentToRange = (
  segment: AnthropicUsageSegmentPayload,
  startTime: number,
  endTime: number
): AnthropicUsageSegmentPayload => ({
  ...segment,
  startTime,
  endTime,
  data: (segment.data ?? []).filter(entry => {
    const ts = entry.timestamp ? Date.parse(entry.timestamp) : Number.NaN;
    if (!Number.isFinite(ts)) return false;
    const tsSec = Math.floor(ts / 1000);
    return tsSec >= startTime && tsSec < endTime;
  })
});

export const buildAnthropicEntryKey = (entry: AnthropicUsageData): string => {
  const cacheCreation = entry.cache_creation ?? {};
  const serverToolUse = entry.server_tool_use ?? {};
  return [
    entry.timestamp ?? '',
    entry.model ?? '',
    entry.workspace_id ?? '',
    entry.api_key_id ?? '',
    entry.service_tier ?? '',
    entry.context_window ?? '',
    entry.inference_geo ?? '',
    entry.speed ?? '',
    entry.uncached_input_tokens ?? 0,
    entry.cache_read_input_tokens ?? 0,
    cacheCreation.input_tokens ?? 0,
    cacheCreation.ephemeral_1h_input_tokens ?? 0,
    cacheCreation.ephemeral_5m_input_tokens ?? 0,
    entry.output_tokens ?? 0,
    serverToolUse.input_tokens ?? 0,
    serverToolUse.web_search_requests ?? 0,
    entry.web_search_uses ?? 0
  ].join('|');
};

export const mergeAnthropicUsageEntries = (
  entries: AnthropicUsageData[],
  requestedStartMs: number,
  requestedEndMs: number
): AnthropicUsageData[] => {
  const unique = new Map<string, AnthropicUsageData>();
  for (const entry of entries) {
    const ts = entry.timestamp ? Date.parse(entry.timestamp) : Number.NaN;
    if (!Number.isFinite(ts)) continue;
    if (ts < requestedStartMs || ts >= requestedEndMs) continue;
    const key = buildAnthropicEntryKey(entry);
    if (!unique.has(key)) {
      unique.set(key, entry);
    }
  }
  return Array.from(unique.values()).sort((a, b) => {
    const aTs = Date.parse(a.timestamp ?? '');
    const bTs = Date.parse(b.timestamp ?? '');
    return aTs - bTs;
  });
};

// ── Main Fetch Logic ─────────────────────────────────

export const fetchUsageWithinRange = async (
  startDate: Date,
  endDate: Date,
  onProgress?: (message: string) => void,
  credentialRevision: string = readAdminCredentialRevision()
): Promise<{ data: AnthropicUsageData[]; partialErrors: string[] }> => {
  const bucketWidth = determineAnthropicBucketWidth(startDate, endDate) as AnthropicBucketWidth;
  const bucketSeconds = BUCKET_SECONDS_BY_WIDTH[bucketWidth];
  const maxBucketsPerRequest = MAX_BUCKETS_BY_WIDTH[bucketWidth];
  const maxSpanSeconds = bucketSeconds * maxBucketsPerRequest;

  const requestedStartSec = toUnixSeconds(startDate);
  const requestedEndSec = toUnixSeconds(endDate);
  const alignedStartSec = Math.floor(requestedStartSec / bucketSeconds) * bucketSeconds;
  const alignedEndSec = Math.ceil(requestedEndSec / bucketSeconds) * bucketSeconds;
  const totalBuckets = Math.max(1, Math.ceil((alignedEndSec - alignedStartSec) / bucketSeconds));
  const boundarySec = getAnthropicFinalizationBoundarySec(bucketWidth);

  onProgress?.(`Resolving Anthropic usage buckets (${bucketWidth}, ${totalBuckets} buckets)...`);

  const cachedRaw = await anthropicCache.getUsageSegments(
    bucketWidth,
    alignedStartSec,
    alignedEndSec,
    credentialRevision
  );
  const cachedSegments = cachedRaw
    .map(segment => segment.data as AnthropicUsageSegmentPayload)
    .filter(segment => (
      segment?.version === ANTHROPIC_USAGE_SEGMENT_CACHE_VERSION &&
      segment.bucketWidth === bucketWidth &&
      Number.isFinite(segment.startTime) &&
      Number.isFinite(segment.endTime) &&
      segment.endTime > alignedStartSec &&
      segment.startTime < alignedEndSec
    ));
  const usableCachedSegments = cachedSegments
    .flatMap(segment => {
      if (segment.startTime < boundarySec && segment.endTime > boundarySec) {
        return [normalizeAnthropicSegmentToRange(
          segment,
          segment.startTime,
          boundarySec
        )];
      }
      return [segment];
    })
    .filter(segment => segment.endTime > segment.startTime);

  // Backfill TTL policy for legacy entries and split crossings at day boundary.
  await Promise.all(
    cachedSegments.flatMap(segment => {
      if (isFinalizedAnthropicSegment(bucketWidth, segment.endTime)) {
        const normalizedSegment = normalizeAnthropicSegmentToRange(
          segment,
          segment.startTime,
          segment.endTime
        );
        return [anthropicCache.cacheUsageSegment(
          bucketWidth,
          normalizedSegment.startTime,
          normalizedSegment.endTime,
          normalizedSegment,
          resolveAnthropicSegmentCacheTtl(bucketWidth, segment.endTime),
          credentialRevision
        )];
      }
      if (segment.startTime < boundarySec && segment.endTime > boundarySec) {
        const finalizedSegment = normalizeAnthropicSegmentToRange(
          segment,
          segment.startTime,
          boundarySec
        );
        return [anthropicCache.cacheUsageSegment(
          bucketWidth,
          segment.startTime,
          boundarySec,
          finalizedSegment,
          resolveAnthropicSegmentCacheTtl(bucketWidth, boundarySec),
          credentialRevision
        )];
      }
      return [];
    })
  );

  const targetRange: TimeRange = { start: alignedStartSec, end: alignedEndSec };
  const coveredRanges = usableCachedSegments.map(segment => ({ start: segment.startTime, end: segment.endTime }));
  const missingRanges = subtractTimeRanges(targetRange, coveredRanges);
  const spanSplitRanges = splitTimeRangesByMaxSpan(missingRanges, maxSpanSeconds);
  const requestRanges = splitTimeRangesAtBoundary(spanSplitRanges, boundarySec);

  const fetchedSegments: AnthropicUsageSegmentPayload[] = [];
  const partialErrors: string[] = [];

  for (let index = 0; index < requestRanges.length; index += 1) {
    const range = requestRanges[index];
    onProgress?.(`Fetching Anthropic usage segments (${index + 1}/${requestRanges.length})...`);

    try {
      const data = await fetchAllAnthropicUsage(
        new Date(range.start * 1000),
        new Date(range.end * 1000),
        bucketWidth,
        ANTHROPIC_GROUP_BY
      );

      const segmentPayload: AnthropicUsageSegmentPayload = {
        version: ANTHROPIC_USAGE_SEGMENT_CACHE_VERSION,
        bucketWidth,
        startTime: range.start,
        endTime: range.end,
        fetchedAt: Date.now(),
        data
      };
      fetchedSegments.push(segmentPayload);
      await anthropicCache.cacheUsageSegment(
        bucketWidth,
        range.start,
        range.end,
        segmentPayload,
        resolveAnthropicSegmentCacheTtl(bucketWidth, range.end),
        credentialRevision
      );
    } catch (error) {
      const isRateLimited = error instanceof AnthropicApiError && error.status === 429;
      if (!isRateLimited) {
        throw error instanceof Error
          ? error
          : new Error('Failed to fetch Anthropic usage segment');
      }
      const rangeStart = new Date(range.start * 1000).toISOString();
      const rangeEnd = new Date(range.end * 1000).toISOString();
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.warn(`[Anthropic Usage] Failed to fetch segment ${rangeStart} - ${rangeEnd}: ${errorMsg}`);
      partialErrors.push(`Segment ${rangeStart} to ${rangeEnd}: ${errorMsg}`);
      // Continue with remaining segments - don't throw
    }

    if (index < requestRanges.length - 1) {
      const jitter = Math.floor(Math.random() * 300);
      await delay(300 + jitter);
    }
  }

  const allSegments = [...usableCachedSegments, ...fetchedSegments];
  const mergedUsage = mergeAnthropicUsageEntries(
    allSegments.flatMap(segment => segment.data ?? []),
    startDate.getTime(),
    endDate.getTime()
  );

  const uncovered = subtractTimeRanges(
    targetRange,
    allSegments.map(segment => ({ start: segment.startTime, end: segment.endTime }))
  );

  debugLog('[Anthropic Usage] Segment cache summary', {
    bucketWidth,
    reusedSegments: cachedSegments.length,
    usableCachedSegments: usableCachedSegments.length,
    fetchedSegments: fetchedSegments.length,
    coverageComplete: uncovered.length === 0,
    requestedRange: {
      start: new Date(alignedStartSec * 1000).toISOString(),
      end: new Date(alignedEndSec * 1000).toISOString()
    },
    mergedItems: mergedUsage.length
  });

  return { data: mergedUsage, partialErrors };
};
